import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ApiConfiguration } from '../api/api-configuration';
import {
  dashboardProjectsControllerGetProjectContributorsV1,
  DashboardProjectsControllerGetProjectContributorsV1$Params,
} from '../api/fn/dashboard-projects/dashboard-projects-controller-get-project-contributors-v-1';

export interface ProjectStats {
  privateRaised: number;
  privateContributors: number;
  publicRaised: number;
  publicContributors: number;
  totalRaised: number;
  totalContributors: number;
}

/**
 * Service for loading and caching project contributor statistics.
 * Shared between countdown-progress-card and raised-amounts-card components.
 */
@Injectable({
  providedIn: 'root',
})
export class ProjectStatsService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiConfiguration);

  // Cache stats per project ID
  private readonly statsCache = new Map<string, ProjectStats>();
  private readonly loadingProjects = new Set<string>();

  // Current project stats as signals
  private readonly _currentProjectId = signal<string | null>(null);
  private readonly _stats = signal<ProjectStats>({
    privateRaised: 0,
    privateContributors: 0,
    publicRaised: 0,
    publicContributors: 0,
    totalRaised: 0,
    totalContributors: 0,
  });
  private readonly _loading = signal<boolean>(false);

  // Public readonly signals
  readonly stats = this._stats.asReadonly();
  readonly loading = this._loading.asReadonly();

  // Computed convenience accessors
  readonly privateRaised = computed(() => this._stats().privateRaised);
  readonly privateContributors = computed(() => this._stats().privateContributors);
  readonly publicRaised = computed(() => this._stats().publicRaised);
  readonly publicContributors = computed(() => this._stats().publicContributors);
  readonly totalRaised = computed(() => this._stats().totalRaised);
  readonly totalContributors = computed(() => this._stats().totalContributors);

  /**
   * Load stats for a project. Uses cache if available.
   * @param projectId - The project ID to load stats for
   * @param forceReload - Force reload even if cached
   */
  async loadStats(projectId: string, forceReload = false): Promise<ProjectStats> {
    if (!projectId) {
      return this.getEmptyStats();
    }

    // Return cached if available and not forcing reload
    if (!forceReload && this.statsCache.has(projectId)) {
      const cached = this.statsCache.get(projectId)!;
      this._currentProjectId.set(projectId);
      this._stats.set(cached);
      return cached;
    }

    // Prevent duplicate loading
    if (this.loadingProjects.has(projectId)) {
      // Wait for existing load to complete
      while (this.loadingProjects.has(projectId)) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      return this.statsCache.get(projectId) || this.getEmptyStats();
    }

    this.loadingProjects.add(projectId);
    this._loading.set(true);
    this._currentProjectId.set(projectId);

    try {
      // Load all stats in parallel
      const [privateRes, publicRes, totalRes] = await Promise.all([
        this.fetchContributorStats(projectId, 'private'),
        this.fetchContributorStats(projectId, 'public'),
        this.fetchContributorStats(projectId),
      ]);

      const stats: ProjectStats = {
        privateRaised: privateRes.totalRaised,
        privateContributors: privateRes.totalContributors,
        publicRaised: publicRes.totalRaised,
        publicContributors: publicRes.totalContributors,
        totalRaised: totalRes.totalRaised,
        totalContributors: totalRes.totalContributors,
      };

      // Cache and update signals
      this.statsCache.set(projectId, stats);
      this._stats.set(stats);

      return stats;
    } catch (error) {
      console.error('Failed to load project stats:', error);
      return this.getEmptyStats();
    } finally {
      this.loadingProjects.delete(projectId);
      this._loading.set(false);
    }
  }

  /**
   * Clear cache for a specific project or all projects
   */
  clearCache(projectId?: string): void {
    if (projectId) {
      this.statsCache.delete(projectId);
    } else {
      this.statsCache.clear();
    }
  }

  private async fetchContributorStats(
    projectId: string,
    round?: 'private' | 'public'
  ): Promise<{ totalRaised: number; totalContributors: number }> {
    const params: DashboardProjectsControllerGetProjectContributorsV1$Params = {
      projectId,
      limit: 1,
      round,
    };

    const response = await firstValueFrom(
      dashboardProjectsControllerGetProjectContributorsV1(
        this.http,
        this.apiConfig.rootUrl,
        params
      )
    );

    return {
      totalRaised: response.body?.totalRaised ?? 0,
      totalContributors: response.body?.totalContributors ?? 0,
    };
  }

  private getEmptyStats(): ProjectStats {
    return {
      privateRaised: 0,
      privateContributors: 0,
      publicRaised: 0,
      publicContributors: 0,
      totalRaised: 0,
      totalContributors: 0,
    };
  }
}
